import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Download, RotateCcw, Save, FileText } from "lucide-react";
import { Btn, Field, Input } from "@/components/Primitives";
import { api, saveBlob, fmtCurrency, fmtNumber, formatApiErrorDetail } from "@/lib/api";

const th = "text-left font-semibold text-stone-700 text-xs uppercase tracking-wider px-3 py-2.5 whitespace-nowrap bg-stone-100";
const td = "px-3 py-2 font-mono text-xs whitespace-nowrap";
// compact Rand (no cents) for wide coverage columns
const fmtR0 = (v) => `${v < 0 ? "-" : ""}R${Math.abs(Math.round(v || 0)).toLocaleString()}`;

function TonsCell({ row, canEdit, onSave }) {
  const [val, setVal] = useState(row.shipped_tons);
  const [dirty, setDirty] = useState(false);
  useEffect(() => { setVal(row.shipped_tons); setDirty(false); }, [row.shipped_tons]);
  if (!canEdit) return <span>{fmtNumber(row.shipped_tons, 3)}</span>;
  return (
    <span className="inline-flex items-center gap-1">
      <input type="number" min="0" step="0.001" value={val}
             onChange={(e) => { setVal(e.target.value); setDirty(true); }}
             className={`w-24 px-2 py-1 border rounded-sm text-xs font-mono text-right focus:outline-none focus:ring-2 focus:ring-emerald-800/30 ${row.shipped_override ? "border-amber-400 bg-amber-50" : "border-stone-300"}`}
             data-testid={`tons-input-${row.month}`} />
      {dirty && (
        <button className="text-emerald-800 hover:text-emerald-950" title="Save override"
                onClick={() => onSave(row.month, val === "" ? null : Number(val))} data-testid={`tons-save-${row.month}`}>
          <Save className="h-3.5 w-3.5" />
        </button>
      )}
      {row.shipped_override && !dirty && (
        <button className="text-stone-400 hover:text-emerald-800" title={`Reset to auto (${fmtNumber(row.shipped_tons_auto, 3)} t from dispatches)`}
                onClick={() => onSave(row.month, null)} data-testid={`tons-reset-${row.month}`}>
          <RotateCcw className="h-3.5 w-3.5" />
        </button>
      )}
    </span>
  );
}

export function TariffModel({ canEdit }) {
  const [model, setModel] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [range, setRange] = useState({ from: "", to: "" });

  const load = useCallback(async () => {
    try {
      const params = {};
      if (range.from) params.from = range.from;
      if (range.to) params.to = range.to;
      const r = await api.get("/costdash/tariff", { params });
      setModel(r.data);
      const s = r.data.settings;
      setForm({
        kg_per_m3: s.kg_per_m3,
        norm_output_m3: s.norm_output_m3,
        norm_variable_per_m3: s.norm_variable_per_m3 ?? "",
      });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  }, [range]);
  useEffect(() => { load(); }, [load]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.put("/costdash/tariff/settings", {
        ...form,
        norm_variable_per_m3: form.norm_variable_per_m3 === "" ? null : Number(form.norm_variable_per_m3),
      });
      toast.success("Tariff settings saved");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const saveTons = async (month, tons) => {
    try {
      await api.put(`/costdash/tariff/overrides/${month}`, { shipped_tons: tons });
      toast.success(tons === null ? `Shipped tonnage for ${month} back to auto` : `Shipped tonnage override saved for ${month}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const exportParams = () => {
    const params = {};
    if (range.from) params.from = range.from;
    if (range.to) params.to = range.to;
    return params;
  };

  const downloadXlsx = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/costdash/tariff.xlsx", { responseType: "blob", params: exportParams() });
      saveBlob(res, "tariff-model.xlsx");
    } catch { toast.error("Download failed"); } finally { setDownloading(false); }
  };

  const downloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const res = await api.get("/costdash/tariff.pdf", { responseType: "blob", params: exportParams() });
      saveBlob(res, "tariff-model.pdf");
    } catch { toast.error("Download failed"); } finally { setDownloadingPdf(false); }
  };

  if (!model || !form) return <div className="text-stone-400 text-sm py-8 text-center" data-testid="tariff-loading">Loading tariff model…</div>;
  const dv = model.derived, vol = model.volumes;

  return (
    <div className="mt-8" data-testid="tariff-model-section">
      <div className="flex items-end justify-between flex-wrap gap-2 mb-1">
        <div>
          <div className="font-display text-xl font-semibold">Tariff Model — Logisticon per m³ (Estcourt)</div>
          <div className="text-xs text-stone-500 mt-0.5">{model.note}</div>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <Field label="Months from">
            <Input type="month" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })}
                   className="w-36" data-testid="tariff-from-month" />
          </Field>
          <Field label="to">
            <Input type="month" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })}
                   className="w-36" data-testid="tariff-to-month" />
          </Field>
          {(range.from || range.to) && (
            <Btn variant="secondary" onClick={() => setRange({ from: "", to: "" })} testid="tariff-range-clear">
              <RotateCcw className="h-3.5 w-3.5" /> All months
            </Btn>
          )}
          <Btn variant="secondary" onClick={downloadPdf} disabled={downloadingPdf} testid="download-tariff-pdf-btn">
            <FileText className="h-4 w-4" /> {downloadingPdf ? "Preparing..." : "PDF"}
          </Btn>
          <Btn variant="secondary" onClick={downloadXlsx} disabled={downloading} testid="download-tariff-xlsx-btn">
            <Download className="h-4 w-4" /> {downloading ? "Preparing..." : "Tariff Model (Excel)"}
          </Btn>
        </div>
      </div>

      {/* Settings */}
      <div className="panel p-4 mt-3" data-testid="tariff-settings">
        <div className="data-label mb-3">Inputs</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 items-end">
          <Field label="Conversion factor (kg per m³)">
            <Input type="number" step="1" value={form.kg_per_m3}
                   onChange={(e) => setForm({ ...form, kg_per_m3: e.target.value })} data-testid="tariff-kg-input" />
          </Field>
          <Field label="Norm output / sales (m³/mo)">
            <Input type="number" step="1" value={form.norm_output_m3}
                   onChange={(e) => setForm({ ...form, norm_output_m3: e.target.value })} data-testid="tariff-norm-output-input" />
          </Field>
          <Field label="Norm variable / m³ (blank = auto)">
            <Input type="number" step="0.01" value={form.norm_variable_per_m3} placeholder={`auto: ${fmtNumber(dv.auto_variable_per_m3)}`}
                   onChange={(e) => setForm({ ...form, norm_variable_per_m3: e.target.value })} data-testid="tariff-norm-variable-input" />
          </Field>
          {canEdit && (
            <Btn onClick={saveSettings} disabled={saving} testid="tariff-save-settings-btn">
              {saving ? "Saving..." : "Save Settings"}
            </Btn>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-x-6 gap-y-3 mt-4 pt-3 border-t border-stone-100 text-xs" data-testid="tariff-derived">
          <div><span className="text-stone-500">Fixed monthly fee (avg of {dv.fee_months_tagged || 0} tagged mo)</span><div className="font-mono font-semibold text-emerald-900" data-testid="tariff-fee-avg">{fmtCurrency(dv.fixed_fee_avg)}</div></div>
          <div><span className="text-stone-500">Norm output in tons / mo</span><div className="font-mono font-semibold">{fmtNumber(dv.norm_output_tons, 1)} t</div></div>
          <div><span className="text-stone-500">Fee at norm output, per m³</span><div className="font-mono font-semibold text-emerald-900">{fmtCurrency(dv.fee_per_m3_at_norm)}</div></div>
          <div><span className="text-stone-500">Norm variable per m³ {dv.variable_is_auto ? "(auto)" : "(set)"}</span><div className="font-mono font-semibold">{fmtCurrency(dv.norm_variable_per_m3)}</div></div>
          <div><span className="text-stone-500">Cost price per m³ (var + fixed + fee)</span><div className="font-mono font-semibold text-emerald-900" data-testid="tariff-cost-per-m3">{fmtCurrency(dv.cost_price_per_m3)}</div></div>
          <div><span className="text-stone-500">Cost price per ton</span><div className="font-mono font-semibold">{fmtCurrency(dv.cost_price_per_ton)}</div></div>
          <div><span className="text-stone-500">Finished stock on site</span><div className="font-mono font-semibold">{fmtNumber(vol.finished_stock_m3)} m³</div></div>
          <div><span className="text-stone-500">Raw / WIP (indicative)</span><div className="font-mono font-semibold">{fmtNumber(vol.raw_wip_m3)} m³</div></div>
          <div><span className="text-stone-500">Total produced incl dispatched</span><div className="font-mono font-semibold" data-testid="tariff-produced-total">{fmtNumber(vol.produced_m3_total)} m³</div></div>
          <div><span className="text-stone-500">Processed by us to date</span><div className="font-mono font-semibold">{fmtNumber(vol.own_processed_m3_total)} m³</div></div>
          <div><span className="text-stone-500">— of which cut donor poles (original + recut)</span><div className="font-mono font-semibold text-teal-800">{fmtNumber(vol.reprocessed_donor_m3)} m³</div></div>
          <div><span className="text-stone-500">— of which recut (handled again on cut day)</span><div className="font-mono font-semibold text-teal-800" data-testid="tariff-recut-m3">{fmtNumber(vol.recut_donor_m3)} m³</div></div>
          <div><span className="text-stone-500">Average CBM per pole</span><div className="font-mono font-semibold">{fmtNumber(vol.avg_cbm_per_pole, 4)} m³</div></div>
        </div>
      </div>

      {/* Scenarios */}
      <div className="font-display text-base font-semibold mt-6 mb-2">Scenarios — Cost Price at Output to Wood Solutions</div>
      <div className="panel overflow-hidden" data-testid="tariff-scenarios-table">
        <div className="overflow-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-stone-100 border-b-2 border-stone-200">
              <tr>{["Output (m³/mo)", "Output (t/mo)", "Fee / m³", "Fee / ton", "Variable / m³", "Cost / m³", "Cost / ton", "Coverage finished", "Coverage incl raw"].map((h, i) => (
                <th key={h} className={`${th} ${i > 0 ? "text-right" : ""}`}>{h}</th>))}</tr>
            </thead>
            <tbody>
              {model.scenarios.map((s) => (
                <tr key={s.volume_m3} className={`border-b border-stone-100 ${s.is_norm ? "bg-emerald-50/70" : "hover:bg-stone-50"}`} data-testid={`scenario-${s.volume_m3}`}>
                  <td className={`${td} font-semibold`}>{fmtNumber(s.volume_m3, 0)}{s.is_norm && <span className="ml-1.5 text-[10px] uppercase text-emerald-800 font-bold">norm</span>}</td>
                  <td className={`${td} text-right`}>{fmtNumber(s.volume_tons, 1)}</td>
                  <td className={`${td} text-right`}>{fmtCurrency(s.fee_per_m3)}</td>
                  <td className={`${td} text-right`}>{fmtCurrency(s.fee_per_ton)}</td>
                  <td className={`${td} text-right`}>{fmtCurrency(s.variable_per_m3)}</td>
                  <td className={`${td} text-right font-semibold text-emerald-900`}>{fmtCurrency(s.cost_per_m3)}</td>
                  <td className={`${td} text-right`}>{fmtCurrency(s.cost_per_ton)}</td>
                  <td className={`${td} text-right`}>{fmtNumber(s.coverage_finished_months)} mo</td>
                  <td className={`${td} text-right`}>{fmtNumber(s.coverage_incl_raw_months)} mo</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Cost splitting — rollup per month (months as columns) */}
      <div className="font-display text-base font-semibold mt-6 mb-1">Cost Splitting — Rollup per Month</div>
      <div className="text-xs text-stone-500 mb-2">
        Tag each cost line in Finance → Costs (Tariff Classification). Fixed and Variable are reported together.
        Tagged amounts are shown ex VAT and count once invoiced — a fully-tagged month reaches an untagged remainder of R0.
      </div>
      <div className="panel overflow-hidden" data-testid="tariff-splitting-table">
        <div className="overflow-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-stone-100 border-b-2 border-stone-200">
              <tr>
                <th className={th}>Rollup per month</th>
                {model.months.map((r) => <th key={r.month} className={`${th} text-right`}>{r.month}</th>)}
              </tr>
            </thead>
            <tbody>
              {[
                ["Tagged Start-up", (r) => fmtCurrency(r.startup_tagged)],
                ["Tagged Variable/Fixed", (r) => fmtCurrency(r.varfix_tagged)],
                ["Tagged Consumables", (r) => fmtCurrency(r.consumables_tagged)],
                ["Tagged Transport", (r) => fmtCurrency(r.transport_tagged)],
                ["Tagged Fee", (r) => fmtCurrency(r.fee_tagged)],
                ["Total Tagged", (r) => fmtCurrency(r.total_tagged), "font-semibold bg-stone-50"],
                ["Invoiced ex VAT", (r) => fmtCurrency(r.invoiced_ex_vat)],
                ["Untagged remainder", (r) => (
                  <span className={Math.abs(r.untagged) > 0.05 ? "text-red-600 font-semibold" : "text-emerald-700"}>{fmtCurrency(r.untagged)}</span>
                )],
                ["Fee check", (r) => (r.fee_check == null ? "—" : fmtCurrency(r.fee_check))],
                ["Processed Ton (by us, incl cut)", (r) => fmtNumber(r.processed_tons, 1)],
                ["Processed m³ (by us, incl cut)", (r) => fmtNumber(r.processed_m3)],
                ["Variable per m³", (r) => (r.variable_per_m3 == null ? "—" : fmtCurrency(r.variable_per_m3))],
              ].map(([label, fn, cls]) => (
                <tr key={label} className={`border-b border-stone-100 hover:bg-stone-50 ${cls || ""}`} data-testid={`splitting-row-${label}`}>
                  <td className={`${td} font-semibold whitespace-nowrap`}>{label}</td>
                  {model.months.map((r) => <td key={r.month} className={`${td} text-right`}>{fn(r)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-3 py-2 bg-emerald-900 text-white text-xs font-mono flex items-center justify-between gap-4" data-testid="avg-variable-line">
          <span className="font-semibold">Average variable tariff over the period (per m³) — same calc as norm variable on Inputs</span>
          <span className="font-bold text-sm">{fmtCurrency(dv.auto_variable_per_m3)}</span>
        </div>
      </div>

      {/* Cost detail breakdown */}
      <div className="font-display text-base font-semibold mt-6 mb-1">Detail: Invoice Lines &amp; Underlying Costs</div>
      <div className="panel overflow-hidden" data-testid="tariff-detail-table">
        <div className="overflow-auto max-h-96">
          <table className="min-w-full text-sm">
            <thead className="bg-stone-100 border-b-2 border-stone-200 sticky top-0 z-10">
              <tr>{["Month", "Invoice No", "Date", "Description", "Amount ex VAT", "Tag Category"].map((h, i) => (
                <th key={h} className={`${th} ${i === 4 ? "text-right" : ""}`}>{h}</th>))}</tr>
            </thead>
            <tbody>
              {(model.detail || []).map((d, i) => (
                <tr key={i} className="border-b border-stone-100 hover:bg-stone-50" data-testid={`detail-row-${i}`}>
                  <td className={`${td} font-semibold`}>{d.month}</td>
                  <td className={td}>{d.invoice_no || "—"}</td>
                  <td className={td}>{d.date}</td>
                  <td className="px-3 py-2 text-xs">{d.description}</td>
                  <td className={`${td} text-right`}>{fmtCurrency(d.amount_ex_vat)}</td>
                  <td className={`${td} ${d.category === "Untagged" ? "text-red-600 font-semibold" : ""}`}>{d.category}</td>
                </tr>
              ))}
              {!(model.detail || []).length && (
                <tr><td className="px-3 py-4 text-center text-stone-400 text-sm" colSpan={6}>No costs captured in this period.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Actual vs norm */}
      <div className="font-display text-base font-semibold mt-6 mb-1">Actual vs Norm (Post-calculation)</div>
      <div className="text-xs text-stone-500 mb-2">
        Actual variable = invoiced − start-up − fee − consumables − transport. Shipped tonnage pulls automatically
        from Dispatch (weighbridge weights); amber = manual override. Positive coverage = actual costs below norm.
      </div>
      <div className="panel overflow-hidden" data-testid="tariff-nacalc-table">
        <div className="overflow-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-stone-100 border-b-2 border-stone-200">
              <tr>{["Month", "Invoiced ex VAT", "Start-up", "Fixed Fee", "Consumables", "Transport", "Actual Variable", "Shipped (t)", "Shipped (m³)", "Actual Var/m³", "Norm Var/m³", "Var Coverage", "Fee Coverage", "Total"].map((h, i) => (
                <th key={h} className={`${th} ${i > 0 ? "text-right" : ""}`}>{h}</th>))}</tr>
            </thead>
            <tbody>
              {model.months.map((r) => (
                <tr key={r.month} className="border-b border-stone-100 hover:bg-stone-50" data-testid={`nacalc-${r.month}`}>
                  <td className={`${td} font-semibold`}>{r.month}</td>
                  <td className={`${td} text-right`}>{fmtCurrency(r.invoiced_ex_vat)}</td>
                  <td className={`${td} text-right text-amber-700`}>{fmtCurrency(r.startup_eff)}</td>
                  <td className={`${td} text-right`}>{fmtCurrency(r.fee_eff)}</td>
                  <td className={`${td} text-right text-orange-700`}>{fmtCurrency(r.consumables_tagged)}</td>
                  <td className={`${td} text-right text-cyan-700`}>{fmtCurrency(r.transport_tagged)}</td>
                  <td className={`${td} text-right`}>{fmtCurrency(r.variable_eff)}</td>
                  <td className={`${td} text-right`}><TonsCell row={r} canEdit={canEdit} onSave={saveTons} /></td>
                  <td className={`${td} text-right`}>{fmtNumber(r.shipped_m3)}</td>
                  <td className={`${td} text-right`}>{r.actual_variable_per_m3 == null ? "—" : fmtCurrency(r.actual_variable_per_m3)}</td>
                  <td className={`${td} text-right`}>{fmtCurrency(r.norm_variable_per_m3)}</td>
                  <td className={`${td} text-right ${r.coverage_variable < 0 ? "text-red-600" : "text-emerald-700"}`}>{fmtR0(r.coverage_variable)}</td>
                  <td className={`${td} text-right ${r.coverage_fee < 0 ? "text-red-600" : "text-emerald-700"}`}>{fmtR0(r.coverage_fee)}</td>
                  <td className={`${td} text-right font-semibold ${r.coverage_total < 0 ? "text-red-600" : "text-emerald-800"}`}>{fmtR0(r.coverage_total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
